import React from 'react';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SectionCard } from '../../../../components/Section';
import COLORS from '../../../../constants/colors';
import { IDerayahSubOrder } from '../../../../types/derayah';
import { DerayahStatusChip } from './DerayahStatusChip';
import { formatOptionalDate, formatRef, formatSar, formatUpTo8 } from './format';

interface Column {
  label: string;
  align?: 'right';
  render: (subOrder: IDerayahSubOrder) => React.ReactNode;
}

/** All amounts come straight from the backend — none are derived in the browser. */
const columns: Column[] = [
  { label: 'Fund code', render: (s) => s.derayahFundId },
  { label: 'Fund Order ID', render: (s) => s.fundOrderId },
  { label: 'Derayah order ref', render: (s) => formatRef(s.derayahOrderRef) },
  { label: 'Status', render: (s) => <DerayahStatusChip status={s.status} /> },
  { label: 'Submitted Amount', align: 'right', render: (s) => formatSar(s.amount) },
  { label: 'Gross Settlement', align: 'right', render: (s) => formatSar(s.executedAmount) },
  { label: 'Invested Principal', align: 'right', render: (s) => formatSar(s.assetValue) },
  { label: 'Commission', align: 'right', render: (s) => formatSar(s.commissionAmount) },
  { label: 'VAT', align: 'right', render: (s) => formatSar(s.vatAmount) },
  { label: 'Total Charges', align: 'right', render: (s) => formatSar(s.totalCharges) },
  { label: 'Unexecuted / Returned Amount', align: 'right', render: (s) => formatSar(s.returnedAmount) },
  { label: 'Execution NAV', align: 'right', render: (s) => formatUpTo8(s.executionPrice) },
  { label: 'Executed Units', align: 'right', render: (s) => formatUpTo8(s.executedUnits) },
  { label: 'Execution date', render: (s) => formatOptionalDate(s.executionDate) },
];

const renderCellValue = (value: React.ReactNode) => {
  if (value === null || value === undefined || value === '') {
    return (
      <Typography component="span" sx={{ color: COLORS.LIGHT_GRAY, fontSize: 'inherit' }}>
        —
      </Typography>
    );
  }
  return value;
};

interface DerayahSubOrdersTableProps {
  subOrders: IDerayahSubOrder[];
}

export function DerayahSubOrdersTable({ subOrders }: DerayahSubOrdersTableProps) {
  return (
    <SectionCard title="Fund executions" disablePadding sx={{ mt: '16px' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.label}
                  align={column.align}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: COLORS.DARK_GREY }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!subOrders.length && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ color: COLORS.LIGHT_GRAY }}>
                  No fund executions
                </TableCell>
              </TableRow>
            )}
            {subOrders.map((subOrder) => (
              <React.Fragment key={`derayah-sub-order-${subOrder.id}`}>
                <TableRow sx={subOrder.errorMessage ? { '& > td': { borderBottom: 0 } } : undefined} hover>
                  {columns.map((column) => (
                    <TableCell key={column.label} align={column.align} sx={{ whiteSpace: 'nowrap' }}>
                      {renderCellValue(column.render(subOrder))}
                    </TableCell>
                  ))}
                </TableRow>
                {subOrder.errorMessage && (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ pt: 0 }}>
                      <Alert severity="error" sx={{ py: 0 }}>
                        {subOrder.errorMessage}
                        {subOrder.failureCount > 0 && ` (failures: ${subOrder.failureCount})`}
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
}
